import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import './TaskPricing.css';

type TaskPricing = {
  rawPhotoCredits: number | null;
  annotatedPhotoCredits: number | null;
  rawVideoCreditsPerMinute: number | null;
};

type TaskStatus = 'active' | 'test' | 'inactive';

type TaskRow = {
  id: string;
  title: string;
  category: string;
  subcategory: string;
  itemType: string;
  mediaType: 'photo' | 'video' | 'unknown';
  status: TaskStatus;
  pricing: TaskPricing | null;
};

const norm = (v: unknown) => String(v ?? '').trim();

function mediaTypeOf(type: unknown): 'photo' | 'video' | 'unknown' {
  const t = norm(type).toLowerCase();
  if (t === 'photo') return 'photo';
  if (t === 'video') return 'video';
  return 'unknown';
}

function statusOf(d: any): TaskStatus {
  if (d.active !== true) return 'inactive';
  if (norm(d.mode).toLowerCase() === 'test') return 'test';
  return 'active';
}

function isUnpriced(row: TaskRow): boolean {
  const p = row.pricing;
  if (!p) return true;
  if (row.mediaType === 'photo') {
    return p.rawPhotoCredits == null || p.annotatedPhotoCredits == null;
  }
  if (row.mediaType === 'video') {
    return p.rawVideoCreditsPerMinute == null;
  }
  return true;
}

const emptyForm = {
  rawPhotoCredits: '',
  annotatedPhotoCredits: '',
  rawVideoCreditsPerMinute: '',
};

export default function TaskPricing() {
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [catFilter, setCatFilter] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const [itemFilter, setItemFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | 'photo' | 'video' | 'unknown'>('');
  const [statusFilter, setStatusFilter] = useState<'' | TaskStatus>('active');
  const [search, setSearch] = useState('');
  const [unpricedOnly, setUnpricedOnly] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'tasks'),
      (snap) => {
        const rows: TaskRow[] = snap.docs.map((docSnap) => {
          const d = docSnap.data() as any;
          return {
            id: docSnap.id,
            title: norm(d.title) || '(no title)',
            category: norm(d.category),
            subcategory: norm(d.subcategory),
            itemType: norm(d.itemType),
            mediaType: mediaTypeOf(d.type),
            status: statusOf(d),
            pricing:
              d.pricing && typeof d.pricing === 'object'
                ? {
                    rawPhotoCredits:
                      typeof d.pricing.rawPhotoCredits === 'number'
                        ? d.pricing.rawPhotoCredits
                        : null,
                    annotatedPhotoCredits:
                      typeof d.pricing.annotatedPhotoCredits === 'number'
                        ? d.pricing.annotatedPhotoCredits
                        : null,
                    rawVideoCreditsPerMinute:
                      typeof d.pricing.rawVideoCreditsPerMinute === 'number'
                        ? d.pricing.rawVideoCreditsPerMinute
                        : null,
                  }
                : null,
          };
        });
        rows.sort((a, b) => {
          const c = a.category.localeCompare(b.category);
          if (c !== 0) return c;
          const s = a.subcategory.localeCompare(b.subcategory);
          if (s !== 0) return s;
          const i = a.itemType.localeCompare(b.itemType);
          if (i !== 0) return i;
          return a.title.localeCompare(b.title);
        });
        setTasks(rows);
        setLoading(false);
      },
      () => {
        setError('Failed to load tasks.');
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const matches = (
    row: TaskRow,
    skip?: 'cat' | 'sub' | 'item' | 'type' | 'status'
  ) => {
    if (skip !== 'cat' && catFilter && row.category !== catFilter) return false;
    if (skip !== 'sub' && subFilter && row.subcategory !== subFilter) return false;
    if (skip !== 'item' && itemFilter && row.itemType !== itemFilter) return false;
    if (skip !== 'type' && typeFilter && row.mediaType !== typeFilter) return false;
    if (skip !== 'status' && statusFilter && row.status !== statusFilter) return false;
    return true;
  };

  const categories = useMemo(() => {
    const s = new Set<string>();
    tasks.filter((r) => matches(r, 'cat')).forEach((r) => {
      if (r.category) s.add(r.category);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, subFilter, itemFilter, typeFilter, statusFilter]);

  const subcategories = useMemo(() => {
    const s = new Set<string>();
    tasks.filter((r) => matches(r, 'sub')).forEach((r) => {
      if (r.subcategory) s.add(r.subcategory);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, catFilter, itemFilter, typeFilter, statusFilter]);

  const itemTypes = useMemo(() => {
    const s = new Set<string>();
    tasks.filter((r) => matches(r, 'item')).forEach((r) => {
      if (r.itemType) s.add(r.itemType);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, catFilter, subFilter, typeFilter, statusFilter]);

  const typeOptions = useMemo(() => {
    const s = new Set<'photo' | 'video' | 'unknown'>();
    tasks.filter((r) => matches(r, 'type')).forEach((r) => s.add(r.mediaType));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, catFilter, subFilter, itemFilter, statusFilter]);

  const statusOptions = useMemo(() => {
    const s = new Set<TaskStatus>();
    tasks.filter((r) => matches(r, 'status')).forEach((r) => s.add(r.status));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, catFilter, subFilter, itemFilter, typeFilter]);

  const activeUnpricedCount = useMemo(
    () => tasks.filter((r) => r.status === 'active' && isUnpriced(r)).length,
    [tasks]
  );

  const filtered = useMemo(() => {
    return tasks
      .filter((r) => matches(r))
      .filter((r) => !unpricedOnly || isUnpriced(r))
      .filter((r) => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return (
          r.title.toLowerCase().includes(q) ||
          r.category.toLowerCase().includes(q) ||
          r.subcategory.toLowerCase().includes(q) ||
          r.itemType.toLowerCase().includes(q)
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, catFilter, subFilter, itemFilter, typeFilter, statusFilter, unpricedOnly, search]);

  const visible = useMemo(() => filtered.slice(0, 200), [filtered]);

  useEffect(() => {
    setSubFilter('');
    setItemFilter('');
  }, [catFilter]);

  useEffect(() => {
    setItemFilter('');
  }, [subFilter]);

  const parseField = (v: string): number | null => {
    const t = v.trim();
    if (t === '') return null;
    const n = Number(t);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n);
  };

  const closeModals = () => {
    setEditingId(null);
    setBulkOpen(false);
    setForm({
      rawPhotoCredits: '',
      annotatedPhotoCredits: '',
      rawVideoCreditsPerMinute: '',
    });
  };

  const openEdit = (row: TaskRow) => {
    setEditingId(row.id);
    setForm({
      rawPhotoCredits:
        row.pricing?.rawPhotoCredits != null ? String(row.pricing.rawPhotoCredits) : '',
      annotatedPhotoCredits:
        row.pricing?.annotatedPhotoCredits != null
          ? String(row.pricing.annotatedPhotoCredits)
          : '',
      rawVideoCreditsPerMinute:
        row.pricing?.rawVideoCreditsPerMinute != null
          ? String(row.pricing.rawVideoCreditsPerMinute)
          : '',
    });
    setError(null);
    setSuccess(null);
  };

  const saveSingle = async () => {
    if (!editingId) return;
    setSaving(true);
    setError(null);
    try {
      await updateDoc(doc(db, 'tasks', editingId), {
        pricing: {
          rawPhotoCredits: parseField(form.rawPhotoCredits),
          annotatedPhotoCredits: parseField(form.annotatedPhotoCredits),
          rawVideoCreditsPerMinute: parseField(form.rawVideoCreditsPerMinute),
          updatedAt: serverTimestamp(),
          updatedBy: 'admin',
        },
      });
      setSuccess('Pricing updated.');
      closeModals();
    } catch {
      setError('Failed to save pricing.');
    } finally {
      setSaving(false);
    }
  };

  const saveBulk = async () => {
    const ids = Array.from(selected);
    const raw = form.rawPhotoCredits.trim();
    const ann = form.annotatedPhotoCredits.trim();
    const vid = form.rawVideoCreditsPerMinute.trim();
    if (!raw && !ann && !vid) {
      setError('Enter at least one price.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const CHUNK = 400;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        const batch = writeBatch(db);
        chunk.forEach((id) => {
          const patch: any = {
            'pricing.updatedAt': serverTimestamp(),
            'pricing.updatedBy': 'admin-bulk',
          };
          if (raw) patch['pricing.rawPhotoCredits'] = parseField(raw);
          if (ann) patch['pricing.annotatedPhotoCredits'] = parseField(ann);
          if (vid) patch['pricing.rawVideoCreditsPerMinute'] = parseField(vid);
          batch.update(doc(db, 'tasks', id), patch);
        });
        await batch.commit();
      }
      setSuccess(`Updated pricing for ${ids.length} tasks.`);
      setSelected(new Set());
      closeModals();
    } catch {
      setError('Failed to apply bulk pricing.');
    } finally {
      setSaving(false);
    }
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const allSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        visible.forEach((r) => next.delete(r.id));
      } else {
        visible.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  const renderPrice = (value: number | null, applicable: boolean) => {
    if (!applicable) return <span className="text-muted">—</span>;
    if (value == null) return <span className="flag-warn">⚠</span>;
    return <span>{value.toLocaleString()}</span>;
  };

  const editingRow = editingId ? tasks.find((t) => t.id === editingId) : null;
  const allVisibleSelected =
    visible.length > 0 && visible.every((r) => selected.has(r.id));

  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <div className="task-pricing-page">
      <header>
        <div>
          <h1>Task Pricing</h1>
          <p>Marketplace credit pricing per task. Tasks without pricing cannot be released.</p>
        </div>
        <div className="header-actions">
          <button
            className="btn-primary"
            disabled={selected.size === 0}
            onClick={() => {
              setForm({
                rawPhotoCredits: '',
                annotatedPhotoCredits: '',
                rawVideoCreditsPerMinute: '',
              });
              setError(null);
              setSuccess(null);
              setBulkOpen(true);
            }}
          >
            Set price for {selected.size} selected
          </button>
        </div>
      </header>

      {error && !editingId && !bulkOpen && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="pricing-stats">
        <div className="pricing-stat">
          <div className="pricing-stat-label">Total Tasks</div>
          <div className="pricing-stat-value">{tasks.length.toLocaleString()}</div>
        </div>
        <div className="pricing-stat">
          <div className="pricing-stat-label">Active &amp; Unpriced</div>
          <div
            className={`pricing-stat-value${activeUnpricedCount > 0 ? ' stat-warn' : ''}`}
          >
            {activeUnpricedCount.toLocaleString()}
          </div>
        </div>
        <div className="pricing-stat">
          <div className="pricing-stat-label">Showing</div>
          <div className="pricing-stat-value">{filtered.length.toLocaleString()}</div>
        </div>
      </div>

      <div className="pricing-filters">
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select value={subFilter} onChange={(e) => setSubFilter(e.target.value)}>
          <option value="">All Subcategories</option>
          {subcategories.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={itemFilter} onChange={(e) => setItemFilter(e.target.value)}>
          <option value="">All Item Types</option>
          {itemTypes.map((i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) =>
            setTypeFilter(e.target.value as '' | 'photo' | 'video' | 'unknown')
          }
        >
          <option value="">All Types</option>
          {typeOptions.map((t) => (
            <option key={t} value={t}>
              {capitalize(t)}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | TaskStatus)}
        >
          <option value="">All Statuses</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {capitalize(s)}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search tasks…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="checkbox">
          <input
            type="checkbox"
            checked={unpricedOnly}
            onChange={(e) => setUnpricedOnly(e.target.checked)}
          />
          Unpriced only
        </label>
      </div>

      {filtered.length > 200 && (
        <div className="pricing-notice">
          Showing first 200 of {filtered.length.toLocaleString()} matches. Narrow the filters to
          see more.
        </div>
      )}

      {loading ? (
        <div className="empty-state">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">No tasks match these filters.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                />
              </th>
              <th>Task</th>
              <th>Category</th>
              <th>Subcategory</th>
              <th>Item Type</th>
              <th>Type</th>
              <th>Status</th>
              <th>Raw</th>
              <th>Annotated</th>
              <th>Video /min</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id} className={isUnpriced(row) ? 'row-unpriced' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(row.id)}
                    onChange={() => toggleOne(row.id)}
                  />
                </td>
                <td>
                  <strong>{row.title}</strong>
                  {isUnpriced(row) && <span className="flag-warn">⚠ No price</span>}
                </td>
                <td className="text-muted">{row.category || '—'}</td>
                <td className="text-muted">{row.subcategory || '—'}</td>
                <td className="text-muted">{row.itemType || '—'}</td>
                <td>
                  <span className="type-badge">{row.mediaType}</span>
                </td>
                <td>
                  <span className={`status-badge status-${row.status}`}>{row.status}</span>
                </td>
                <td>
                  {renderPrice(
                    row.pricing?.rawPhotoCredits ?? null,
                    row.mediaType === 'photo'
                  )}
                </td>
                <td>
                  {renderPrice(
                    row.pricing?.annotatedPhotoCredits ?? null,
                    row.mediaType === 'photo'
                  )}
                </td>
                <td>
                  {renderPrice(
                    row.pricing?.rawVideoCreditsPerMinute ?? null,
                    row.mediaType === 'video'
                  )}
                </td>
                <td>
                  <button className="btn-link" onClick={() => openEdit(row)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editingId && editingRow && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Edit Pricing</h2>
              <button type="button" className="modal-close" onClick={closeModals} aria-label="Close">
                ×
              </button>
            </div>
            <p className="modal-sub">{editingRow.title}</p>
            {error && <div className="alert alert-error">{error}</div>}
            <label className="modal-field">
              Raw Photo Credits
              <input
                type="number"
                min={0}
                placeholder="Leave blank for no price"
                value={form.rawPhotoCredits}
                disabled={editingRow.mediaType !== 'photo'}
                onChange={(e) => setForm((f) => ({ ...f, rawPhotoCredits: e.target.value }))}
              />
              {editingRow.mediaType !== 'photo' && (
                <span className="modal-hint">
                  Not applicable to {editingRow.mediaType} tasks
                </span>
              )}
            </label>
            <label className="modal-field">
              Annotated Photo Credits
              <input
                type="number"
                min={0}
                placeholder="Leave blank for no price"
                value={form.annotatedPhotoCredits}
                disabled={editingRow.mediaType !== 'photo'}
                onChange={(e) =>
                  setForm((f) => ({ ...f, annotatedPhotoCredits: e.target.value }))
                }
              />
              {editingRow.mediaType !== 'photo' && (
                <span className="modal-hint">
                  Not applicable to {editingRow.mediaType} tasks
                </span>
              )}
            </label>
            <label className="modal-field">
              Video Credits per Minute
              <input
                type="number"
                min={0}
                placeholder="Leave blank for no price"
                value={form.rawVideoCreditsPerMinute}
                disabled={editingRow.mediaType !== 'video'}
                onChange={(e) =>
                  setForm((f) => ({ ...f, rawVideoCreditsPerMinute: e.target.value }))
                }
              />
              {editingRow.mediaType !== 'video' && (
                <span className="modal-hint">
                  Not applicable to {editingRow.mediaType} tasks
                </span>
              )}
            </label>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-primary"
                disabled={saving}
                onClick={saveSingle}
              >
                {saving ? 'Saving…' : 'Save Pricing'}
              </button>
              <button type="button" className="btn-secondary" onClick={closeModals}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkOpen && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Set Price for {selected.size} Tasks</h2>
              <button type="button" className="modal-close" onClick={closeModals} aria-label="Close">
                ×
              </button>
            </div>
            <p className="modal-sub">
              Blank fields are left unchanged. Only filled fields are applied.
            </p>
            {error && <div className="alert alert-error">{error}</div>}
            <label className="modal-field">
              Raw Photo Credits
              <input
                type="number"
                min={0}
                placeholder="Leave blank for no price"
                value={form.rawPhotoCredits}
                onChange={(e) => setForm((f) => ({ ...f, rawPhotoCredits: e.target.value }))}
              />
            </label>
            <label className="modal-field">
              Annotated Photo Credits
              <input
                type="number"
                min={0}
                placeholder="Leave blank for no price"
                value={form.annotatedPhotoCredits}
                onChange={(e) =>
                  setForm((f) => ({ ...f, annotatedPhotoCredits: e.target.value }))
                }
              />
            </label>
            <label className="modal-field">
              Video Credits per Minute
              <input
                type="number"
                min={0}
                placeholder="Leave blank for no price"
                value={form.rawVideoCreditsPerMinute}
                onChange={(e) =>
                  setForm((f) => ({ ...f, rawVideoCreditsPerMinute: e.target.value }))
                }
              />
            </label>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-primary"
                disabled={saving}
                onClick={saveBulk}
              >
                Apply to {selected.size} Tasks
              </button>
              <button type="button" className="btn-secondary" onClick={closeModals}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
